import { DEFAULT_KEY_BINDINGS, keysToActions, type ControlAction } from '@digger/shared';

export interface KeyboardHooks {
  /** Space / Escape — latch emergency stop. */
  onEstop: () => void;
  /** R — clear the E-STOP latch. */
  onEstopReset: () => void;
  /** V / Tab — swap dominant camera. */
  onSwap: () => void;
  /** Held-key set changed (any tracked key up/down or a forced clear). */
  onChange?: () => void;
}

export const ESTOP_CODES = ['Space', 'Escape'] as const;
export const SWAP_CODES = ['KeyV', 'Tab'] as const;
export const ESTOP_RESET_CODE = 'KeyR';

/**
 * Tracks the set of currently-held control keys. Never fires commands on
 * keydown — the ticker samples `actions()` at a fixed rate. Any way the
 * page can lose the operator's fingers (blur, tab hidden, unload) clears
 * the held set so the next tick sends a zero vector.
 */
export class KeyboardTracker {
  private readonly held = new Set<string>();
  private detach: (() => void) | undefined;

  constructor(
    private readonly hooks: KeyboardHooks,
    private readonly bindings: Readonly<Record<string, ControlAction>> = DEFAULT_KEY_BINDINGS,
  ) {}

  attach(target: Window): void {
    const down = (e: KeyboardEvent) => this.handleKeyDown(e);
    const up = (e: KeyboardEvent) => this.handleKeyUp(e);
    const clear = () => this.clear();
    const visibility = () => {
      if (target.document.visibilityState === 'hidden') this.clear();
    };
    target.addEventListener('keydown', down);
    target.addEventListener('keyup', up);
    target.addEventListener('blur', clear);
    target.addEventListener('pagehide', clear);
    target.document.addEventListener('visibilitychange', visibility);
    this.detach = () => {
      target.removeEventListener('keydown', down);
      target.removeEventListener('keyup', up);
      target.removeEventListener('blur', clear);
      target.removeEventListener('pagehide', clear);
      target.document.removeEventListener('visibilitychange', visibility);
    };
  }

  dispose(): void {
    this.detach?.();
    this.detach = undefined;
    this.clear();
  }

  handleKeyDown(e: KeyboardEvent): void {
    if ((ESTOP_CODES as readonly string[]).includes(e.code)) {
      e.preventDefault();
      this.hooks.onEstop();
      return;
    }
    if ((SWAP_CODES as readonly string[]).includes(e.code)) {
      e.preventDefault(); // Tab must not move focus out of the console
      if (!e.repeat) this.hooks.onSwap();
      return;
    }
    if (e.code === ESTOP_RESET_CODE) {
      if (!e.repeat) this.hooks.onEstopReset();
      return;
    }
    if (this.bindings[e.code]) {
      e.preventDefault();
      if (!this.held.has(e.code)) {
        this.held.add(e.code);
        this.hooks.onChange?.();
      }
    }
  }

  handleKeyUp(e: KeyboardEvent): void {
    if (this.held.delete(e.code)) this.hooks.onChange?.();
  }

  /** Forced release of everything (blur, tab hidden, disconnect, dispose). */
  clear(): void {
    if (this.held.size === 0) return;
    this.held.clear();
    this.hooks.onChange?.();
  }

  /** Currently-held key codes (copy — callers cannot mutate tracker state). */
  get heldCodes(): ReadonlySet<string> {
    return new Set(this.held);
  }

  /** Active control actions derived from the held keys. */
  actions(): Set<ControlAction> {
    return keysToActions(this.held, this.bindings);
  }
}
